<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    :show-kaia-logo="false"
    :show-lang-picker="false"
    theme="blue-background"
  >
    <template #header_start>
      <ButtonBack @click="emit('back')" />
    </template>

    <div class="kds-main-wrapper">
      <img
        v-if="imageUrl"
        class="reflection-image"
        :src="imageUrl"
        :width="imageWidth"
        :height="imageHeight"
        alt=""
      />
      <div class="kds-content-block">
        <p
          v-if="titleKey"
          class="kds-typography-title-xlarge kds-typography-center"
          v-html="$t(titleKey)"
        />
        <p
          class="kds-typography-paragraph-large kds-typography-center"
          v-html="$t(bodyKey)"
        />
      </div>
    </div>

    <template #actions>
      <BaseButton
        :text="$t('eu__self_test_reflection__cta')"
        data-qa="submit"
        @click="handleNextEvent"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import BaseButton from '@shared/components/BaseButton.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import ROUTE from '@shared/modules/assessment/router/names'
import env from '@shared/env.js'
import {
  PAIN_LOCATION_OPTIONS,
  PAIN_DURATION_OPTIONS_MSK_EU,
  PAIN_DURATION_OPTIONS_OTHER,
  PREVIOUS_TREATMENT_OPTIONS,
} from '@shared/modules/assessment/config/selfTestOptions'

defineProps({
  progress: {
    type: Number,
    default: null,
  },
  handleNextEvent: {
    type: Function,
    default: () => {},
  },
})

const emit = defineEmits(['back'])

const route = useRoute()
const selfTestStore = useSelfTestStore()

const keys = computed(() => {
  switch (route.name) {
    case ROUTE.ASSESSMENT_SELF_TEST_PRE_SIGNUP_REFLECTION_PAIN_LOCATION:
      return (
        PAIN_LOCATION_OPTIONS.find(
          (o) => o.key === selfTestStore.worstPainLocation,
        ) ?? null
      )

    case ROUTE.ASSESSMENT_SELF_TEST_PRE_SIGNUP_REFLECTION_PAIN_DURATION: {
      const options = env.isMskEu
        ? PAIN_DURATION_OPTIONS_MSK_EU
        : PAIN_DURATION_OPTIONS_OTHER
      return (
        options.find((o) => o.value === selfTestStore.chronificationLevel) ??
        null
      )
    }

    case ROUTE.ASSESSMENT_SELF_TEST_PRE_SIGNUP_REFLECTION_PREVIOUS_TREATMENT:
      return (
        PREVIOUS_TREATMENT_OPTIONS.find(
          (o) => o.key === selfTestStore.previousTreatment,
        ) ?? null
      )

    default:
      return null
  }
})

const titleKey = computed(() => keys.value?.reflectionTitle ?? null)
const bodyKey = computed(() => keys.value?.reflectionBody ?? '')
const imageUrl = computed(() => keys.value?.image ?? null)
const imageWidth = computed(() => keys.value?.imageWidth)
const imageHeight = computed(() => keys.value?.imageHeight)
</script>

<style scoped lang="scss">
.reflection-image {
  display: block;
  width: 100%;
  max-width: 400px;
  height: auto;
  margin: 0 auto;
  border: 1px solid $kds-color-background-blue-accent;
  border-radius: $kds-space-1x;
}
</style>
